import styled, { css } from 'styled-components'
import { black, darkGrey, darkOrange, mutedGrey, white } from '@/theme/colors'
import { flatButton } from '@/theme/recipes'
import { media, motion, radii } from '@/theme/tokens'
import { text } from '@/theme/typography'
import { Button } from '@components/Button'
import { destinationColor } from '../destinationPalette'

const CELL_GAP = 4
const NAV_BUTTON = 28

/** A month at a time, as tall as the month is: nothing scrolls. */
export const CalendarBlock = styled.div`
	flex: 0 1 380px;
	min-width: 0;
	max-width: 100%;

	/* Stacked over the legend in its own column, where the basis would read as a height. */
	${media.aboveTabletWide} {
		flex: none;
	}
`

/** The arrows' columns hold their room on a one-page calendar too, so the title never leaves the centre. */
export const CalendarHeader = styled.div`
	display: grid;
	grid-template-columns: ${NAV_BUTTON}px minmax(0, 1fr) ${NAV_BUTTON}px;
	align-items: center;
	gap: 8px;
	min-height: ${NAV_BUTTON}px;
`

export const PageTitle = styled.div`
	grid-column: 2;
	${text.labelSm}
	color: ${black};
	text-align: center;
	white-space: nowrap;
`

export const NavButton = styled(Button).attrs({ variant: 'icon' as const })<{ $side: 'previous' | 'next' }>`
	grid-column: ${({ $side }) => ($side === 'previous' ? 1 : 3)};
	grid-row: 1;
	width: ${NAV_BUTTON}px;
	height: ${NAV_BUTTON}px;
	color: ${darkOrange};
`

const weekGrid = css`
	display: grid;
	grid-template-columns: repeat(7, minmax(0, 1fr));
	gap: ${CELL_GAP}px;
`

export const WeekdayRow = styled.div`
	${weekGrid}
	margin-top: 8px;
`

export const WeekdayLetter = styled.span`
	${text.caption}
	color: ${mutedGrey};
	text-align: center;
`

export const Weeks = styled.div`
	margin-top: ${CELL_GAP}px;
	display: flex;
	flex-direction: column;
	gap: ${CELL_GAP}px;
`

export const WeekRowGrid = styled.div`
	${weekGrid}
`

/** Square: a cell is as tall as a seventh of the calendar is wide. */
const squareCell = css`
	width: 100%;
	aspect-ratio: 1;
`

export const BlankCell = styled.span`
	${squareCell}
`

/** A day of the month outside the trip: a bare grey number, not a control. */
export const OutsideCell = styled.span`
	${squareCell}
	display: flex;
	align-items: center;
	justify-content: center;
	${text.caption}
	color: ${darkGrey};
`

/**
 * A trip day's cell (a raw button by design, like the create-trip calendar's):
 * the destination tint, or the full destination colour on the selected day.
 */
export const DayCell = styled.button<{ $selected: boolean; $colorIndex: number }>`
	${flatButton}
	${squareCell}
	border-radius: ${radii.small};
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 2px;
	${text.caption}
	transition: background-color ${motion.base};

	${({ $selected, $colorIndex }) => {
		const palette = destinationColor($colorIndex)
		return $selected
			? css`
					background: ${palette.fill};
					color: ${white};
					font-weight: 700;
				`
			: css`
					background: ${palette.tint};
					color: ${black};

					&:hover {
						background: ${palette.tintHover};
					}
				`
	}}
`

/** The event-presence mark under the number: the destination colour, white on the selected day. */
export const EventMark = styled.span<{ $visible: boolean; $selected: boolean; $colorIndex: number }>`
	width: 4px;
	height: 4px;
	border-radius: ${radii.round};
	background: ${({ $selected, $colorIndex }) => ($selected ? white : destinationColor($colorIndex).fill)};
	opacity: ${({ $visible }) => ($visible ? 1 : 0)};
`
